import React from 'react';
import { Link } from 'react-router-dom';
import '../css/About.css';
import NXTesting from '../assets/NXTesting.png';
import PickAndPlace from '../assets/PickAndPlace.png';
import LaserEngraving from '../assets/LaserEngraving.png';
import Milling from '../assets/Milling.png';
import Engineering from '../assets/Engineering.png';
import Assembly from '../assets/Assembly.png';
import SeoHead from './Shared/SeoHead';

const About = () => {
    const siteUrl = window.location.origin;
    const organizationSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'MHE Electronics',
        url: `${siteUrl}/about`,
        logo: `${siteUrl}/MHELogo.svg`,
        email: 'sales@mhe.co.za',
        telephone: '+27 31 780 2560',
        description: 'MHE Electronics is the original developer and manufacturer of proprietary electronic control systems, warning equipment, and custom OEM electronics.',
        address: {
            '@type': 'PostalAddress',
            streetAddress: '49 Alexander Road, Westmead',
            addressLocality: 'Pinetown',
            addressRegion: 'KwaZulu-Natal',
            postalCode: '3608',
            addressCountry: 'ZA'
        }
    };

    return (
        <div className="about-page">
            <SeoHead
                title="About MHE Electronics | Original Developer and Manufacturer"
                description="Learn about MHE Electronics, the original developer and manufacturer of electronic control systems, warning equipment, and custom OEM electronics."
                canonical={`${siteUrl}/about`}
                schema={organizationSchema}
            />

            {/* ===== Hero ===== */}
            <div className="about-hero">
                <span className="about-hero-eyebrow">About MHE Electronics</span>
                <h1 className="about-hero-title">
                    Precision electronics, <span>manufactured right</span>
                </h1>
                <p className="about-hero-sub">
                    MHE Electronics is the original developer and manufacturer of proprietary electronic control systems,
                    warning equipment, and custom-built electronic solutions for OEMs and industrial customers.
                </p>
            </div>

            {/* ===== Stats ===== */}
            <div className="about-stats">
                <div className="about-stat">
                    <span className="about-stat-value">30+</span>
                    <span className="about-stat-label">Years in business</span>
                </div>
                <div className="about-stat">
                    <span className="about-stat-value">120+</span>
                    <span className="about-stat-label">Staff members</span>
                </div>
                <div className="about-stat">
                    <span className="about-stat-value">2</span>
                    <span className="about-stat-label">Global offices</span>
                </div>
            </div>

            <div className="about-content">

                {/* ===== Who we are ===== */}
                <section className="about-who">
                    <div className="about-who-copy">
                        <span className="about-section-eyebrow">Who we are</span>
                        <h2 className="about-section-title">Advanced manufacturing capabilities</h2>
                        <p>
                            For over 30 years, MHE Electronics has designed, developed, and manufactured electronic control systems,
                            sirens, and warning equipment in-house. We are not only a supplier of finished products — we are the
                            engineering and manufacturing source behind our own product range.
                        </p>
                        <p>
                            With a dedicated workforce of over 120 staff members, we provide end-to-end production capability
                            for both proprietary MHE products and OEM customer projects. From in-house PCB design and prototyping
                            to full-scale assembly, testing, and volume manufacturing, we deliver precision and reliability at every stage.
                        </p>
                    </div>

                    <div className="about-who-pillars">
                        <div className="about-pillar">
                            <div className="about-pillar-icon">
                                <i className="bi bi-cpu" />
                            </div>
                            <h4>Electronic Design</h4>
                            <p>In-house engineering teams developing custom PCB and control system solutions.</p>
                        </div>
                        <div className="about-pillar">
                            <div className="about-pillar-icon">
                                <i className="bi bi-diagram-3" />
                            </div>
                            <h4>Control Systems</h4>
                            <p>Intelligent controllers, switch panels, and vehicle-integrated control units.</p>
                        </div>
                        <div className="about-pillar">
                            <div className="about-pillar-icon">
                                <i className="bi bi-tools" />
                            </div>
                            <h4>Custom Manufacturing</h4>
                            <p>Tailored electronic assemblies and wiring harnesses built to client specifications.</p>
                        </div>
                        <div className="about-pillar">
                            <div className="about-pillar-icon">
                                <i className="bi bi-arrow-repeat" />
                            </div>
                            <h4>Full Lifecycle</h4>
                            <p>From design and prototyping to volume production and global distribution.</p>
                        </div>
                    </div>
                </section>

                <section className="about-authority-section">
                    <span className="about-section-eyebrow">Authority and source</span>
                    <h2 className="about-section-title">The official manufacturer source</h2>
                    <p className="about-section-sub">
                        MHE Electronics publishes manufacturer-issued information about our company, our product range, and our
                        technical capabilities on this website. This helps customers, partners, and search engines identify MHE
                        Electronics as the original developer and sole manufacturing source for our proprietary solutions.
                    </p>
                    <div className="about-authority-grid">
                        <div className="about-authority-card">
                            <h3>Original developer</h3>
                            <p>Our engineering teams design and develop proprietary MHE electronic control systems and warning products in-house.</p>
                        </div>
                        <div className="about-authority-card">
                            <h3>In-house manufacturer</h3>
                            <p>Products are assembled, tested, and manufactured within MHE Electronics facilities using dedicated in-house capability.</p>
                        </div>
                        <div className="about-authority-card">
                            <h3>Technical source of truth</h3>
                            <p>This website is intended to provide official manufacturer information, product context, and supporting technical resources.</p>
                        </div>
                    </div>
                    <p className="about-section-sub" style={{ marginTop: '24px' }}>
                        Our products are manufactured by MHE Electronics and supplied through a trusted network of
                        authorised distributors. See our <Link to="/distributors" className="distributor-inline-link">authorised distributors</Link> page
                        for the official list of supply partners.
                    </p>
                </section>

                {/* ===== Facility Photos ===== */}
                <section>
                    <span className="about-section-eyebrow">Our facility</span>
                    <h2 className="about-section-title">Inside the manufacturing floor</h2>
                    <p className="about-section-sub">
                        A look inside our state of the art facility where every product is designed, assembled, and tested in-house.
                    </p>
                    <div className="about-facility-grid">
                        <div className="about-facility-card">
                            <img src={PickAndPlace} alt="Pick and place assembly" className="about-facility-img" />
                            <span className="about-facility-label">
                                <i className="bi bi-cpu" /> Pick &amp; Place Assembly
                            </span>
                        </div>
                        <div className="about-facility-card">
                            <img src={NXTesting} alt="NX product testing" className="about-facility-img" />
                            <span className="about-facility-label">
                                <i className="bi bi-clipboard2-check" /> Product Testing
                            </span>
                        </div>
                        <div className="about-facility-card">
                            <img src={LaserEngraving} alt="Laser engraving" className="about-facility-img" />
                            <span className="about-facility-label">
                                <i className="bi bi-stars" /> Laser Engraving
                            </span>
                        </div>
                        <div className="about-facility-card">
                            <img src={Milling} alt="CNC milling" className="about-facility-img" />
                            <span className="about-facility-label">
                                <i className="bi bi-gear" /> CNC Milling
                            </span>
                        </div>
                        <div className="about-facility-card">
                            <img src={Assembly} alt="Assembly" className="about-facility-img" />
                            <span className="about-facility-label">
                                <i className="bi bi-boxes" /> Assembly
                            </span>
                        </div>
                        <div className="about-facility-card">
                            <img src={Engineering} alt="Engineering" className="about-facility-img" />
                            <span className="about-facility-label">
                                <i className="bi bi-tools" /> Engineering
                            </span>
                        </div>
                    </div>
                </section>

                {/* ===== Offices ===== */}
                <section>
                    <span className="about-section-eyebrow">Our offices</span>
                    <h2 className="about-section-title">Get in touch</h2>
                    <p className="about-section-sub">
                        MHE Electronics operates through two key locations — our head office and manufacturing
                        facility in Pinetown, South Africa, and our distribution and support base in Cowlinge,
                        Suffolk, United Kingdom.
                    </p>

                    <div className="about-offices-grid" style={{ marginTop: '28px' }}>

                        {/* South Africa */}
                        <div className="about-office-card">
                            <div className="about-office-header">
                                <span className="about-office-flag">🇿🇦</span>
                                <div className="about-office-header-text">
                                    <h3>South Africa</h3>
                                    <span>Head Office &amp; Manufacturing · Pinetown, KwaZulu-Natal</span>
                                </div>
                            </div>
                            <div className="about-office-body">
                                <div className="about-office-detail">
                                    <div className="about-office-detail-icon">
                                        <i className="bi bi-geo-alt" />
                                    </div>
                                    <div className="about-office-detail-text">
                                        <span>Address</span>
                                        <span>49 Alexander Road, Westmead, Pinetown, KwaZulu-Natal, 3608</span>
                                    </div>
                                </div>
                                <div className="about-office-detail">
                                    <div className="about-office-detail-icon">
                                        <i className="bi bi-telephone" />
                                    </div>
                                    <div className="about-office-detail-text">
                                        <span>Telephone</span>
                                        <a href="tel:+27317802560">+27 31 780 2560</a>
                                    </div>
                                </div>
                                <div className="about-office-detail">
                                    <div className="about-office-detail-icon">
                                        <i className="bi bi-envelope" />
                                    </div>
                                    <div className="about-office-detail-text">
                                        <span>Email</span>
                                        <a href="mailto:sales@mhe.co.za">sales@mhe.co.za</a>
                                    </div>
                                </div>
                            </div>
                            <iframe title="MHE South Africa Location" className="about-office-map"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1041.7150882390254!2d30.83542771808612!3d-29.821198313456204!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ef6fe93b4bf4001%3A0xfd7d416f1cf1f608!2s49+Alexander+Rd%2C+Westmead%2C+Pinetown%2C+3608!5e0!3m2!1sen!2sza!4v1566297880331!5m2!1sen!2sza"
                                allowFullScreen loading="lazy"
                            />
                        </div>

                        {/* United Kingdom */}
                        <div className="about-office-card">
                            <div className="about-office-header">
                                <span className="about-office-flag">🇬🇧</span>
                                <div className="about-office-header-text">
                                    <h3>United Kingdom</h3>
                                    <span>Distribution &amp; Support · Cowlinge, Suffolk</span>
                                </div>
                            </div>
                            <div className="about-office-body">
                                <div className="about-office-detail">
                                    <div className="about-office-detail-icon">
                                        <i className="bi bi-geo-alt" />
                                    </div>
                                    <div className="about-office-detail-text">
                                        <span>Address</span>
                                        <span>Fairstead House, Erratts Hill, Cowlinge, Newmarket, Suffolk, CB8 9QD</span>
                                    </div>
                                </div>
                                <div className="about-office-detail">
                                    <div className="about-office-detail-icon">
                                        <i className="bi bi-telephone" />
                                    </div>
                                    <div className="about-office-detail-text">
                                        <span>Telephone</span>
                                        <a href="tel:+441440820381">+44 1440 820381</a>
                                    </div>
                                </div>
                                <div className="about-office-detail">
                                    <div className="about-office-detail-icon">
                                        <i className="bi bi-envelope" />
                                    </div>
                                    <div className="about-office-detail-text">
                                        <span>Email</span>
                                        <a href="mailto:mike@mheuk.co.uk">mike@mheuk.co.uk</a>
                                    </div>
                                </div>
                            </div>
                            <iframe title="MHE UK Location" className="about-office-map"
                                src="https://maps.google.com/maps?q=52.24827507026405,0.40884789567578567&z=15&output=embed"
                                allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>

                    </div>
                </section>

            </div>
        </div>
    );
};

export default About;